/**
 * PhaseTransitionToast — brief banner that fades in whenever the active
 * `gameState.phase` flips to a new value.
 *
 * Why this exists: the simplified-play autopilot in Play.tsx silently
 * advances through draft-plan → document-deploy → execute-tests →
 * incident-fortify on the player's behalf. Without a visible signal the
 * player presses "End Phase" and sees nothing change — the game looks
 * broken. This toast names the phase the player has just moved into and
 * shows a one-line plain-English explanation of *what* that phase does
 * for ~2.4s, then bows out, so the autopilot's work is both *seen* and
 * *understood* by a brand-new player.
 *
 * Visuals:
 *   - cream paper card (matches the parchment surface system)
 *   - 4px phase-tinted left border (the same colour family used by the
 *     PhaseChip in StatusBar)
 *   - small phase icon + bold uppercase label, "» " arrow prefix for forward
 *     motion, with a single-line body-font explanation underneath
 *   - fixed bottom-center, 88px above the viewport bottom (clears the
 *     TurnBadge/PhaseControls bar at 16+padding)
 *   - z-index above the map but below modals (zIndex 6)
 *
 * Behaviour:
 *   - Only fires on phase TRANSITIONS, not on first paint. A ref tracks
 *     whether we've seen any phase before; the very first non-null phase
 *     is silently absorbed.
 *   - Each transition assigns a fresh `key` so AnimatePresence treats
 *     consecutive toasts as distinct elements (so a double-tap of End Phase
 *     animates as two distinct toasts rather than one frozen card).
 *   - Auto-clears after TOAST_VISIBLE_MS (~2.4s). If a new phase arrives
 *     mid-toast, the new toast replaces the old one with no flicker.
 *
 * Honours `prefers-reduced-motion`: skips the slide and the scale, fades
 * straight in/out.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import type { TurnPhase } from '@qd/protocol';
import { fontStacks, playerColors, surfaces } from '@qd/design-tokens';
import { useTurnPhase } from '../../stores/gameStore';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  DocumentDeployIcon,
  DraftPlanIcon,
  ExecuteTestsIcon,
  IncidentFortifyIcon,
} from './icons';

// Player-facing phase titles. The `incident-fortify` phase used to read
// "Wrap Up" but a first-time player ("the user's friend") explicitly
// described it as "defense / koruma" — i.e. the bookend opposite of the
// attack phase. We adopt that mental model: the player has just finished
// attacking, now we close out the turn while their territories effectively
// "hold the line". Calling it "Defense Phase" makes the attack/defense
// pairing immediately legible to a brand-new player.
//
// Exported because the mobile phase-info popover (MobilePhaseInfoPopover)
// renders the same title above the explanation body — keeping a single
// canonical source means the educational copy never drifts between
// surfaces.
export const PHASE_LABELS: Readonly<Record<TurnPhase, string>> = {
  'draft-plan': 'Reinforcing',
  'document-deploy': 'Deploying Documents',
  'execute-tests': 'Attack Phase',
  'incident-fortify': 'Defense Phase',
};

// Phase tint palette — exported so mobile surfaces can reuse the same
// border-accent colour family the toast and desktop StatusBar use.
export const PHASE_TINTS: Readonly<Record<TurnPhase, string>> = {
  'draft-plan': playerColors.mustard.hex,
  'document-deploy': playerColors.prussian.hex,
  'execute-tests': surfaces.waxRed,
  'incident-fortify': playerColors.verdigris.hex,
};

// One-line plain-English explanations for each phase, shown beneath the
// uppercase label in the toast. Keep these short — they have to read in
// ~2.4s while a new player is also looking at the map.
export const PHASE_EXPLANATIONS: Readonly<Record<TurnPhase, string>> = {
  'draft-plan': 'Reinforcement cubes auto-place on your weakest territory',
  'document-deploy': 'Auto-progressing — no action needed from you',
  'execute-tests':
    'Your turn to attack — click your territory, then an adjacent enemy',
  'incident-fortify': 'Wrapping up the turn — auto-progressing',
};

// Bumped from 1.6s to give a first-time player time to actually read the
// explanation line beneath the title. 2.4s is the upper edge of "comfortably
// readable for a 12-word line" without lingering long enough to feel
// blocking on repeat plays — and the toast has pointer-events: none on its
// wrapper so it never intercepts a click.
const TOAST_VISIBLE_MS = 2400;

interface RecentToast {
  readonly phase: TurnPhase;
  readonly key: number;
}

function PhaseIcon({ phase, color }: { phase: TurnPhase; color: string }): ReactNode {
  switch (phase) {
    case 'draft-plan':
      return <DraftPlanIcon size={20} color={color} />;
    case 'document-deploy':
      return <DocumentDeployIcon size={20} color={color} />;
    case 'execute-tests':
      return <ExecuteTestsIcon size={20} color={color} />;
    case 'incident-fortify':
      return <IncidentFortifyIcon size={20} color={color} />;
  }
}

export function PhaseTransitionToast(): ReactNode {
  const phase = useTurnPhase();
  const reduced = useReducedMotion();
  const isMobile = useIsMobile();

  // The phase we showed last; used to detect transitions.
  const lastPhaseRef = useRef<TurnPhase | null>(null);
  // True once we've absorbed the first non-null phase. Prevents the toast
  // from firing on initial mount.
  const seenFirstRef = useRef<boolean>(false);
  // Bumped on every transition so AnimatePresence treats each toast as a
  // distinct child (even if the same phase recurs back-to-back, e.g. after
  // a state replay).
  const keyRef = useRef<number>(0);

  const [recent, setRecent] = useState<RecentToast | null>(null);

  useEffect(() => {
    if (phase === null) {
      // Disconnected / pre-attach: reset so we don't surprise-toast on the
      // first frame of a fresh attach.
      lastPhaseRef.current = null;
      seenFirstRef.current = false;
      setRecent(null);
      return;
    }

    if (!seenFirstRef.current) {
      seenFirstRef.current = true;
      lastPhaseRef.current = phase;
      return;
    }

    if (lastPhaseRef.current === phase) return; // unchanged

    lastPhaseRef.current = phase;
    keyRef.current += 1;
    setRecent({ phase, key: keyRef.current });
  }, [phase]);

  useEffect(() => {
    if (recent === null) return;
    const id = window.setTimeout(() => {
      // Only clear if this is still the current toast — guards against a
      // race where a newer phase arrived before this timer fired.
      setRecent((cur) => (cur && cur.key === recent.key ? null : cur));
    }, TOAST_VISIBLE_MS);
    return () => window.clearTimeout(id);
  }, [recent]);

  return (
    <div
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        // On mobile, the MobileActionBar (88px tall, z-index 12) sits at
        // bottom: 0. A naive `bottom: 88` toast lands directly under that bar
        // on the stacking order (the bar's z-index of 12 wins over our 6),
        // so the toast becomes invisible. Push the toast above the action bar
        // (100px = 88px bar + 12px gap), pad for the iOS home-indicator safe
        // area, and bump z-index to 14 so it sits above the action bar (12)
        // but well below modals (~100).
        bottom: isMobile ? 'calc(100px + env(safe-area-inset-bottom, 0px))' : 88,
        zIndex: isMobile ? 14 : 6,
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
      aria-live="polite"
      aria-atomic="true"
    >
      <AnimatePresence mode="wait">
        {recent && (
          <motion.div
            key={recent.key}
            initial={
              reduced
                ? { opacity: 0 }
                : { opacity: 0, y: 14, scale: 0.96 }
            }
            animate={
              reduced
                ? { opacity: 1 }
                : { opacity: 1, y: 0, scale: 1 }
            }
            exit={
              reduced
                ? { opacity: 0 }
                : { opacity: 0, y: -8, scale: 0.98 }
            }
            transition={
              reduced
                ? { duration: 0.18 }
                : { duration: 0.32, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] }
            }
            style={{
              minWidth: 240,
              maxWidth: 'min(86vw, 460px)',
              padding: '10px 16px 11px 14px',
              background: surfaces.paper,
              borderLeft: `4px solid ${PHASE_TINTS[recent.phase]}`,
              borderTop: `1px solid ${surfaces.paperShadow}`,
              borderRight: `1px solid ${surfaces.paperShadow}`,
              borderBottom: `1px solid ${surfaces.paperShadow}`,
              borderRadius: 4,
              color: surfaces.ink,
              boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              pointerEvents: 'auto',
            }}
          >
            <PhaseIcon phase={recent.phase} color={PHASE_TINTS[recent.phase]} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 6,
                  fontFamily: fontStacks.mono,
                  fontSize: 12,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  lineHeight: 1.1,
                }}
              >
                <span style={{ color: PHASE_TINTS[recent.phase], fontWeight: 700 }}>
                  {'»'}
                </span>
                <span>{PHASE_LABELS[recent.phase]}</span>
              </div>
              <div
                style={{
                  fontFamily: fontStacks.body,
                  fontSize: 11.5,
                  letterSpacing: 0,
                  textTransform: 'none',
                  fontWeight: 400,
                  color: surfaces.ink,
                  opacity: 0.78,
                  lineHeight: 1.35,
                }}
              >
                {PHASE_EXPLANATIONS[recent.phase]}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
